import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    MOH_RISK_LEVEL,
    AMLRISK_STATUSES,
    CustomerSummaryTabSummaryOptionsState,
    DECEASED_LEVEL,
    ID_MISSION_STATUSES,
    ID_MISSION_RESULT_STATUSES,
    type CustomerOptionType,
} from 'src/components/CustomersBar/CustomerTab/CustomerSummaryTab/CustomerSummaryTabSummaryOptions/CustomerSummaryTabSummaryOptions.state';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import {
    BadgeColored,
    RiskBadgeColored,
    ClosureCodes,
} from 'src/components/CustomersBar/CustomerTab/CustomerSummaryTab/CustomerSummaryTab.style';
import { pofWalletAggregate } from 'src_server/types/accountData';
import { CustomerSummaryTabAffordability } from 'src/components/CustomersBar/CustomerTab/CustomerSummaryTab/CustomerSummaryTabAffordability/CustomerSummaryTabAffordability';
import { OpenapiProxyStaffAccountAffordabilityResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_account_affordability';
import { getDepositLimits } from 'src_server/types/responsibleGambling';
import { getTransactionsTotalDeposits } from 'src_server/types/wallet';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

interface CustomerSummaryTabSummaryOptionsType {
    customerId: number;
    pofWalletAggregateResource: Resource<pofWalletAggregate.ResponseType> | null;
    affordabilityStatusResource: Resource<OpenapiProxyStaffAccountAffordabilityResponse200Type>;
    depositLimitsResource: Resource<getDepositLimits.ResponseType> | null;
    transactionTotalDepositsResource: Resource<getTransactionsTotalDeposits.ResponseType>;
}

export const CustomerSummaryTabSummaryOptions = observer(
    'CustomerSummaryTabSummaryOptions',
    ({
        customerId,
        pofWalletAggregateResource,
        affordabilityStatusResource,
        depositLimitsResource,
        transactionTotalDepositsResource,
    }: CustomerSummaryTabSummaryOptionsType) => {
        const { common, models } = useAppStateContext();

        const configState = ConfigState.get(common);
        const {
            complianceRestrictionSummary,
            srgEnabled: isSrgEnabled,
            pof: isPof,
            closureCodes: isClosureCodes,
            isPEP,
            depositRestriction: isDepositRestriction,
            markersOfHarm: isMarkersOfHarm,
            identity,
            idMission,
            deceased: isDeceased,
            sourceOfFunds: isSourceOfFunds,
        } = configState.config;

        const [state] = React.useState(
            () =>
                new CustomerSummaryTabSummaryOptionsState(
                    customerId,
                    models,
                    pofWalletAggregateResource,
                    transactionTotalDepositsResource,
                    common,
                    configState
                )
        );

        return (
            <>
                <ul className='customer-summary-options'>
                    {
                        <li key='status'>
                            <strong data-test='blocked-label'>Blocked:</strong>
                            <span
                                className={state.getClassName('status')}
                                data-test='account-status-blocked'
                            >
                                {state.basicData?.status === 'active' ? 'No' : 'Yes'}
                            </span>
                            {isClosureCodes && state.basicData?.status === 'closed' ? (
                                <ClosureCodes
                                    className={state.getClassName('status')}
                                    data-test='account-status-closure-code'
                                >
                                    {state.basicData.closureReasonId}
                                </ClosureCodes>
                            ) : null}
                        </li>
                    }
                    {state.customerOptionsFirstColumn.map((item: CustomerOptionType) => (
                        <li key={item.id}>
                            <strong>{item.label}:</strong>
                            <span
                                className={state.getClassName(item.id)}
                                data-test={`account-status-${item.label.toLowerCase().replaceAll(' ', '-').replaceAll('.', '')}`}
                            >
                                {item.loading === true ? (
                                    <div className='inline-flex items-center justify-center w-[32px] h-[1em] align-middle'>
                                        <div className='w-3 h-3 border-2 border-dashed rounded-full animate-spin border-foreground' />
                                    </div>
                                ) : (
                                    item.value
                                )}
                            </span>
                        </li>
                    ))}
                    <li>
                        <strong>HVC:</strong>
                        <span
                            className={state.getClassName('isHVC')}
                            data-test='account-status-hvc'
                        >
                            {state.basicData?.isHVC === true ? 'Yes' : 'No'}
                        </span>
                    </li>

                    {complianceRestrictionSummary || isSrgEnabled ? (
                        <li>
                            <strong>C. Restriction</strong>
                            {state.pendingSuspensionTimeActive ? (
                                <BadgeColored
                                    badgeColor='red'
                                    data-test='account-status-compliance-restriction'
                                >
                                    YES
                                </BadgeColored>
                            ) : (
                                <BadgeColored
                                    badgeColor='green'
                                    data-test='account-status-compliance-restriction'
                                >
                                    NO
                                </BadgeColored>
                            )}
                        </li>
                    ) : null}
                    {isDepositRestriction ? (
                        <li>
                            <strong data-test='deposit-restriction-label'>D. Restriction</strong>
                            {state.pendingDepositRestrictionActive ? (
                                <BadgeColored
                                    badgeColor='red'
                                    data-test='account-status-deposit-restriction'
                                >
                                    YES
                                </BadgeColored>
                            ) : (
                                <BadgeColored
                                    badgeColor='green'
                                    data-test='account-status-deposit-restriction'
                                >
                                    NO
                                </BadgeColored>
                            )}
                        </li>
                    ) : null}
                    {isPEP ? (
                        <li>
                            <strong>PEP:</strong>
                            <span
                                className={state.getClassName('pep')}
                                data-test='account-status-pep'
                            >
                                {state.modifyOptionalBooleanStatus(state.basicData?.kycWatchlist?.pep)}
                            </span>
                        </li>
                    ) : null}
                    {isPEP ? (
                        <li>
                            <strong>Sanctions:</strong>
                            <span
                                className={state.getClassName('sanctions')}
                                data-test='account-status-sanctions'
                            >
                                {state.modifyOptionalBooleanStatus(state.basicData?.kycWatchlist?.sanctions)}
                            </span>
                        </li>
                    ) : null}

                    <li>
                        <strong data-test='aml-risk-label'>AML Risk:</strong>
                        <span
                            className={state.getClassName('amlRisk')}
                            data-test='account-status-aml-risk'
                        >
                            {state.objectContains(AMLRISK_STATUSES, state.basicData?.amlRisk ?? 'Low', 'Low')}
                        </span>
                    </li>
                    {isMarkersOfHarm ? (
                        <li>
                            <strong data-test='interaction-risk-level-label'>SG Risk:</strong>
                            <RiskBadgeColored
                                badgeColor={state.getClassName('mohRiskLevel')}
                                data-test='account-status-interaction-risk-level'
                            >
                                {state.objectContains(MOH_RISK_LEVEL, state.getInteractionsListRisk, 'none')}
                            </RiskBadgeColored>
                        </li>
                    ) : null}
                </ul>
                <ul className='customer-summary-options'>
                    {state.customerOptionsSecondColumn.map((item) => (
                        <li key={item.id}>
                            <strong>{item.label}:</strong>
                            <span
                                className={state.getClassName(item.id)}
                                data-test={`account-status-${item.label.toLowerCase().replaceAll(' ', '-').replaceAll('.', '')}`}
                            >
                                {item.value}
                            </span>
                        </li>
                    ))}
                    {isSrgEnabled ? (
                        <li key='srgStatus'>
                            <strong>SRG Status:</strong>
                            <span
                                className={state.getClassName('srgStatus')}
                                data-test='account-status-srg-status'
                            >
                                {(state.basicData?.srgStatus ?? 'not-started').replace('-', ' ')}
                            </span>
                        </li>
                    ) : null}
                    {isPof ? (
                        <li>
                            <strong>POF Status:</strong>
                            <span
                                className={state.getClassName('pofStatus')}
                                data-test='account-status-pof-status'
                            >
                                {(state.basicData?.pofStatus ?? 'not-checked').replace('-', ' ')}
                            </span>
                        </li>
                    ) : null}

                    {isSourceOfFunds ? (
                        <li>
                            <strong>SOF Status:</strong>
                            <span
                                className={state.getClassName('sofStatus')}
                                data-test='account-status-sof-status'
                            >
                                {(state.sofStatus ?? 'not-checked').replace('-', ' ')}
                            </span>
                        </li>
                    ) : null}

                    {isPof ? (
                        <li>
                            <strong>POF net losses:</strong>
                            <span
                                className='badge'
                                data-test='account-status-pof-net-losses'
                            >
                                {state.profit() ?? state.pofTotalNetLoss}
                            </span>
                        </li>
                    ) : null}

                    {isPof ? (
                        <li>
                            <strong>POF t. deposits:</strong>
                            <span
                                className='badge'
                                data-test='account-status-pof-total-deposits'
                            >
                                {
                                    state.totalDeposits() ??
                                        state.parseNumberView(
                                            state.pofWalletAggregateData?.totalDeposit
                                        ) /*todo remove after ?? when pof migrated <==> when flag newPofSrg: true on all operators*/
                                }
                            </span>
                        </li>
                    ) : null}

                    <CustomerSummaryTabAffordability
                        affordabilityStatusResource={affordabilityStatusResource}
                        depositLimitsResource={depositLimitsResource}
                    />

                    {identity === true ? (
                        <>
                            <li>
                                <strong data-test='identity-score-label'>Identity Score:</strong>
                                <span
                                    className={state.getClassName('identityScore')}
                                    data-test='account-status-identity-score'
                                >
                                    {state.basicData?.identityScore ?? 'NOT CHECKED'}
                                </span>
                            </li>
                            <li>
                                <strong data-test='ekyc-label'>Identity Result:</strong>
                                <span
                                    className={state.getClassName('identityResult')}
                                    data-test='account-status-ekyc'
                                >
                                    {state.basicData?.identityResult ?? 'NOT CHECKED'}
                                </span>
                            </li>
                        </>
                    ) : null}
                    {isDeceased ? (
                        <li>
                            <strong data-test='deceased-status-label'>Deceased:</strong>
                            <span
                                className={state.getClassName('deceasedStatus')}
                                data-test='account-status-deceased-status'
                            >
                                {state.objectContains(
                                    DECEASED_LEVEL,
                                    state.basicData?.deceasedStatus ?? 'not-checked',
                                    'not-checked'
                                )}
                            </span>
                        </li>
                    ) : null}
                    {idMission ? (
                        <>
                            <li>
                                <strong data-test='id-mission-result-label'>EKYC Result:</strong>
                                <span
                                    className={state.getClassName('idMissionResult')}
                                    data-test='id-mission-result-status'
                                >
                                    {state.objectContains(
                                        ID_MISSION_RESULT_STATUSES,
                                        state.auditData?.verification.verificationResult ?? 'not-checked',
                                        'Not checked'
                                    )}
                                </span>
                            </li>
                            <li>
                                <strong data-test='id-mission-poa-label'>ID Mission POA:</strong>
                                <span
                                    className={state.getClassName('idMissionPoa')}
                                    data-test='id-mission-poa-status'
                                >
                                    {state.objectContains(
                                        ID_MISSION_STATUSES,
                                        state.auditData?.verification.verificationPoaStatus ?? 'not-checked',
                                        'Not checked'
                                    )}
                                </span>
                            </li>
                            <li>
                                <strong data-test='id-mission-id-card-label'>ID Mission IDCard:</strong>
                                <span
                                    className={state.getClassName('idMissionIdCard')}
                                    data-test='id-mission-id-card-status'
                                >
                                    {state.objectContains(
                                        ID_MISSION_STATUSES,
                                        state.auditData?.verification.verificationIdStatus ?? 'not-checked',
                                        'Not checked'
                                    )}
                                </span>
                            </li>
                            <li>
                                <strong data-test='id-mission-face-id-label'>ID Mission FaceID:</strong>
                                <span
                                    className={state.getClassName('idMissionFaceId')}
                                    data-test='id-mission-face-id-status'
                                >
                                    {state.objectContains(
                                        ID_MISSION_STATUSES,
                                        state.auditData?.verification.verificationFaceStatus ?? 'not-checked',
                                        'Not checked'
                                    )}
                                </span>
                            </li>
                        </>
                    ) : null}
                </ul>
            </>
        );
    }
);
